import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, Mail, Lock, User2 } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Auth() {
    const { signup, login, logout, isAuthed } = useApp();
    const navigate = useNavigate();
    const location = useLocation();
    const [mode, setMode] = useState("signup");
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", password: "" });

    // Privacy guard: if a user reaches this page with a stale session from
    // a previous user on the same device/browser, drop that session immediately
    // so this form is the only thing they see. Prevents one person's profile
    // bleeding into another person's login flow.
    useEffect(() => {
        if (isAuthed) logout();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (mode === "signup") {
                if (!form.name.trim()) throw new Error("Please enter your name");
                await signup(form);
                toast.success("Welcome to Owed", { description: "Let's personalize your scan." });
                navigate("/onboarding");
            } else {
                await login({ email: form.email, password: form.password });
                toast.success("Signed in");
                const from = location.state?.from || "/app/dashboard";
                navigate(from);
            }
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || "Something went wrong";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-hero relative overflow-hidden">
            <div aria-hidden className="absolute top-20 -left-40 h-[420px] w-[420px] rounded-full bg-accent/15 blur-[120px]" />
            <div aria-hidden className="absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-primary/10 blur-[140px]" />

            <div className="relative mx-auto max-w-5xl px-5 sm:px-8">
                <header className="h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18"/><path d="M17 7H9.5a3.5 3.5 0 0 0 0 7H14a3.5 3.5 0 0 1 0 7H6"/></svg>
                        </span>
                        <span className="font-serif text-xl tracking-tight">Owed</span>
                    </Link>
                    <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">Back to site</Link>
                </header>

                <div className="grid lg:grid-cols-2 gap-10 items-center py-10">
                    <div className="hidden lg:block">
                        <p className="text-xs font-mono uppercase tracking-[0.22em] text-muted-foreground">Welcome</p>
                        <h1 className="mt-4 font-serif text-5xl xl:text-6xl leading-[1.05] tracking-tight text-balance">
                            Let's find what's <span className="italic text-primary">rightfully yours.</span>
                        </h1>
                        <p className="mt-5 text-muted-foreground max-w-md leading-relaxed">
                            Sign in to keep your prepared packets, questions, and recovered money in one calm place.
                        </p>
                        <div className="mt-8 space-y-3 text-sm">
                            {[
                                "AI prepares every application from your vault",
                                "Ask you follow-up questions only when needed",
                                "You e-sign; we generate the submission packet",
                                "$5 max for premium. No upsells, ever.",
                            ].map((f) => (
                                <div key={f} className="flex items-start gap-2">
                                    <ShieldCheck className="h-4 w-4 text-success mt-0.5 shrink-0" /> <span>{f}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <Card className="p-8 sm:p-10 border-border shadow-elegant">
                        <div className="flex p-1 rounded-full bg-secondary w-fit">
                            {["signup", "login"].map((m) => (
                                <button key={m} onClick={() => setMode(m)} className={cn("px-4 py-1.5 rounded-full text-xs font-medium transition-colors", mode === m ? "bg-foreground text-background" : "text-muted-foreground")} data-testid={`auth-tab-${m}`}>
                                    {m === "signup" ? "Sign up" : "Sign in"}
                                </button>
                            ))}
                        </div>
                        <h2 className="mt-6 font-serif text-3xl leading-tight">{mode === "signup" ? "Start your free scan" : "Welcome back"}</h2>
                        <p className="mt-2 text-sm text-muted-foreground">{mode === "signup" ? "We use this to match programs — never sell it." : "Pick up where you left off."}</p>

                        <form onSubmit={submit} className="mt-6 space-y-4">
                            {mode === "signup" && (
                                <div>
                                    <Label htmlFor="name" className="text-sm">Name</Label>
                                    <div className="relative mt-2">
                                        <User2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Alex Morgan" className="pl-9 h-11 rounded-xl" data-testid="auth-name-input" />
                                    </div>
                                </div>
                            )}
                            <div>
                                <Label htmlFor="email" className="text-sm">Email</Label>
                                <div className="relative mt-2">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input id="email" type="email" required autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="pl-9 h-11 rounded-xl" data-testid="auth-email-input" />
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="password" className="text-sm">Password</Label>
                                    {mode === "login" && (
                                        <Link
                                            to="/forgot-password"
                                            className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                                            data-testid="auth-forgot-password-link"
                                        >
                                            Forgot password?
                                        </Link>
                                    )}
                                </div>
                                <div className="relative mt-2">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input id="password" type="password" required autoComplete={mode === "signup" ? "new-password" : "current-password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="pl-9 h-11 rounded-xl" minLength={6} data-testid="auth-password-input" />
                                </div>
                                {mode === "signup" && <p className="mt-1.5 text-xs text-muted-foreground">At least 6 characters.</p>}
                            </div>

                            <Button type="submit" disabled={loading} className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 mt-2" data-testid="auth-submit-btn">
                                {loading ? "Please wait…" : mode === "signup" ? "Create account" : "Sign in"}
                                <ArrowRight className="h-4 w-4 ml-1" />
                            </Button>
                        </form>

                        <p className="mt-6 text-xs text-muted-foreground text-center">
                            By continuing you agree to Owed's terms. We don't sell your data — ever.
                        </p>
                    </Card>
                </div>
            </div>
        </div>
    );
}
